/**
 * 세금계산서 역발행 — 레거시 `reverseTax.js` 핵심 포팅
 */
import {
  useBulkDeleteTaxInvoices,
  useCancelTaxInvoices,
  useDeleteTaxInvoice,
  useDownloadBulkTransfer,
  useExportTaxInvoices,
  useIssueTaxInvoices,
  useResetPublishedSettlement,
  useTaxInvoiceList,
  useUploadTaxInvoices,
} from '@/entities/tax-invoice/hooks/useTaxInvoices';
import {
  fromTaxInvoiceWriteDate,
  isTaxInvoiceExportEmptyError,
} from '@/entities/tax-invoice/api/tax-invoice.api';
import { EmptyState } from '@/shared/components/EmptyState';
import { DataCard } from '@/shared/components/DataCard';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { DatePicker, MonthPicker } from '@/shared/components/DatePicker';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { useListPagination } from '@/shared/hooks/useListPagination';
import { formatDate } from '@/shared/lib/dateFormat';
import { formatDateValue } from '@/shared/lib/datePickerUtils';
import { Button } from '@/shared/components/ui/button';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import {
  Ban,
  Download,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { getCurrentRefYm } from '@/pages/fee-rates/feeRateUtils';
import { exportBulkTransferXlsx } from '@/pages/settlements/bulkTransferExportUtils';
import { parseReverseTaxExcel } from '@/pages/settlements/reverseTaxExcelParser';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { FilterRow } from '@/shared/components/ui/row';
import { useUIStore } from '@/stores/uiStore';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

const SOURCE_LABELS: Record<string, string> = {
  SETTLEMENT: '정산',
  EXCEL: '추가',
  MANUAL: '수동',
};

/** 서버 TaxInvoiceIssueStatus 코드 → 화면 라벨 */
const ISSUE_STATUS_LABELS: Record<string, string> = {
  READY: '발행 대기',
  COMPLETED: '발행 완료',
  FAILED: '발행 실패',
};

export const ReverseTaxPage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [yearMonthInput, setYearMonthInput] = useState(getCurrentRefYm());
  const [keywordInput, setKeywordInput] = useState('');
  const [sourceTypeInput, setSourceTypeInput] = useState('all');
  // 초기화할 작성일자. 목록이 비어 있으면 오늘(로컬)로 둔다 —
  // toISOString()은 UTC라 KST 오전 9시 전엔 전날이 된다
  const [writeDate, setWriteDate] = useState(() =>
    formatDateValue(new Date()),
  );
  // 목록에서 맞춘 작성일자. 선택기 값이 이와 다르면 역발행 전에 재확인한다
  const [syncedWriteDate, setSyncedWriteDate] = useState(writeDate);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState<{
    yearMonth?: string;
    keyword?: string;
    sourceType?: string;
  }>({ yearMonth: getCurrentRefYm() });
  const { page, setPage, pageSize, setPageSize, pageable } =
    useListPagination(50);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useTaxInvoiceList(search, pageable);
  const issueInvoices = useIssueTaxInvoices();
  const deleteInvoice = useDeleteTaxInvoice();
  const bulkDelete = useBulkDeleteTaxInvoices();
  const cancelInvoices = useCancelTaxInvoices();
  const uploadTaxInvoices = useUploadTaxInvoices();
  const resetPublished = useResetPublishedSettlement();
  const downloadBulkTransfer = useDownloadBulkTransfer();
  const exportInvoices = useExportTaxInvoices();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = exportInvoices.isPending;

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  useEffect(() => {
    setGlobalLoading(isExporting, '세금계산서 역발행 엑셀을 준비하는 중...');
    return () => setGlobalLoading(false);
  }, [isExporting, setGlobalLoading]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  // 정산년월 단위로 작성일자가 하나로 생성되므로 첫 행 값을 목록의 작성일자로 본다
  const listWriteDate = fromTaxInvoiceWriteDate(rows[0]?.writeDate);

  // 조회·초기화로 목록 작성일자가 바뀌면 초기화할 작성일자를 목록 값에 맞춘다
  useEffect(() => {
    if (!listWriteDate) return;
    setWriteDate(listWriteDate);
    setSyncedWriteDate(listWriteDate);
  }, [listWriteDate]);
  // 조회된 내역이 없으면 다운로드 버튼을 비활성화한다
  const canDownload = rows.length > 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch({
      yearMonth: yearMonthInput.trim() || undefined,
      keyword: keywordInput.trim() || undefined,
      sourceType: sourceTypeInput === 'all' ? undefined : sourceTypeInput,
    });
    setPage(1);
    setSelectedIds([]);
  };

  /** 정산년월 MonthPicker 변경 시 즉시 목록 재조회 */
  const handleYearMonthChange = (next: string) => {
    const ym = next.trim() || getCurrentRefYm();
    setYearMonthInput(ym);
    setSearch((prev) => ({
      ...prev,
      yearMonth: ym || undefined,
    }));
    setPage(1);
    setSelectedIds([]);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleIssue = async () => {
    if (selectedIds.length === 0) {
      toast.error('발행할 항목을 선택해 주세요.');
      return;
    }
    // 역발행은 각 행의 작성일자로 발행된다(초기화할 작성일자는 쓰지 않음).
    // 선택기 값을 바꿨다면 그 날짜로 발행된다고 오해하지 않도록 한 번 더 묻는다
    if (writeDate !== syncedWriteDate) {
      // 선택한 행들의 실제 작성일자를 보여 준다. 다른 페이지에서 고른 행만 있으면 목록 기준값으로 대신한다
      const selectedWriteDates = Array.from(
        new Set(
          rows
            .filter((row) => selectedIds.includes(row.id))
            .map((row) => fromTaxInvoiceWriteDate(row.writeDate))
            .filter(Boolean),
        ),
      );
      const listWriteDateLabel = (
        selectedWriteDates.length > 0 ? selectedWriteDates : [syncedWriteDate]
      )
        .map((value) => formatDate(value))
        .join(', ');
      const ok = await confirm({
        title: '역발행 작성일자 확인',
        description: `초기화할 작성일자(${formatDate(writeDate)})는 역발행에 적용되지 않습니다. 목록의 작성일자(${listWriteDateLabel})로 역발행하시겠습니까?`,
        confirmLabel: '역발행',
      });
      if (!ok) return;
    }
    try {
      await issueInvoices.mutateAsync(selectedIds);
      toast.success('역발행 처리를 요청했습니다.');
      setSelectedIds([]);
      refetch();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: '삭제 확인',
      description: '삭제하시겠습니까?',
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteInvoice.mutateAsync(id);
      toast.success('삭제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const ok = await confirm({
      title: '일괄 삭제',
      description: `${selectedIds.length}건을 삭제하시겠습니까?`,
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await bulkDelete.mutateAsync(selectedIds);
      toast.success('일괄 삭제했습니다.');
      setSelectedIds([]);
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  /**
   * 잘못 올린 역발행을 팝빌에서 취소·삭제하고 발행 대기로 되돌린다(선택 삭제는 우리 DB 행만 지운다).
   * 서버는 발행 완료이면서 팝빌 문서번호가 있는 행만 처리하고, 건별 결과를 canceled/failed 로 나눠 준다.
   */
  const handleCancelIssued = async () => {
    if (selectedIds.length === 0) return;
    const ok = await confirm({
      title: '역발행 발행 취소',
      description: `선택한 ${selectedIds.length}건의 팝빌 역발행 문서를 취소·삭제하고 발행 대기로 되돌립니다. 국세청 전송까지 끝난 문서는 취소되지 않습니다. 진행하시겠습니까?`,
      confirmLabel: '발행 취소',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      const { canceledIds, failedIds } =
        await cancelInvoices.mutateAsync(selectedIds);
      const skippedCount =
        selectedIds.length - canceledIds.length - failedIds.length;
      const skippedNote =
        skippedCount > 0
          ? ` (발행 완료가 아니거나 팝빌 문서가 없는 ${skippedCount}건 제외)`
          : '';
      if (failedIds.length > 0) {
        toast.error(
          `${canceledIds.length}건을 발행 취소했고, ${failedIds.length}건은 취소하지 못했습니다.${skippedNote}`,
        );
      } else if (canceledIds.length > 0) {
        toast.success(
          `${canceledIds.length}건을 발행 취소하고 발행 대기로 되돌렸습니다.${skippedNote}`,
        );
      } else {
        toast.info(
          '발행 취소할 항목이 없습니다. 발행 완료된 항목만 취소할 수 있습니다.',
        );
      }
      setSelectedIds([]);
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleExportExcel = async () => {
    if (!search.yearMonth) {
      toast.error('정산 년월을 선택해 주세요.');
      return;
    }
    try {
      const archive = await exportInvoices.mutateAsync({
        yearMonth: search.yearMonth,
        keyword: search.keyword,
        sourceType: search.sourceType,
      });
      if (!archive.downloadUrl) {
        toast.info('다운로드할 데이터가 없습니다.');
        return;
      }
      openPresignedFile(archive.downloadUrl);
      toast.success(
        `세금계산서 역발행 목록을 다운로드했습니다. (${archive.rowCount.toLocaleString()}건)`,
      );
    } catch (e) {
      if (isTaxInvoiceExportEmptyError(e)) {
        toast.info('다운로드할 데이터가 없습니다.');
        return;
      }
      handleApiError(e, toast);
    }
  };

  const handleResetPublished = async () => {
    if (!search.yearMonth) {
      toast.error('정산 년월을 선택해 주세요.');
      return;
    }
    try {
      await resetPublished.mutateAsync({
        settlementYearMonth: search.yearMonth,
        writeDate,
      });
      toast.success('정산 정보가 초기화되었습니다.');
      setResetDialogOpen(false);
      // 초기화로 목록이 다시 만들어지므로 이전 선택은 모두 해제한다
      setSelectedIds([]);
      refetch();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleBulkTransferDownload = async () => {
    if (!search.yearMonth) {
      toast.error('정산 년월을 선택해 주세요.');
      return;
    }
    try {
      const data = await downloadBulkTransfer.mutateAsync(search.yearMonth);
      if (!data?.length) {
        toast.error('조회된 정보가 없습니다.');
        return;
      }
      exportBulkTransferXlsx(data, search.yearMonth);
      toast.success('대량 이체 데이터를 다운로드했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleUploadExcel = async () => {
    if (!uploadFile) {
      toast.error('엑셀 파일을 선택해 주세요.');
      return;
    }
    try {
      const items = await parseReverseTaxExcel(uploadFile);
      if (items.length === 0) {
        toast.error('업로드할 데이터가 없습니다.');
        return;
      }
      await uploadTaxInvoices.mutateAsync(items);
      toast.success(`${items.length}건을 업로드했습니다.`);
      setUploadOpen(false);
      setUploadFile(null);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
      refetch();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Receipt}
        title="세금계산서 역발행"
        description="세금계산서 역발행 내역 조회 및 발행"
        actions={
          <>
            <MenuWriteGate>
              <Field label="초기화할 작성일자">
                <DatePicker
                  value={writeDate}
                  onChange={setWriteDate}
                  className="w-40"
                />
              </Field>
              <Button
                variant="outline"
                onClick={() => {
                  if (rows.length === 0) {
                    void handleResetPublished();
                  } else {
                    setResetDialogOpen(true);
                  }
                }}
                disabled={!search.yearMonth || resetPublished.isPending}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                정산 정보로 초기화
              </Button>
            </MenuWriteGate>
            <Button
              variant="outline"
              onClick={() => void handleExportExcel()}
              disabled={!canDownload || isExporting || isLoading}
            >
              {isExporting ? '처리 중...' : '엑셀 다운로드'}
            </Button>
            <MenuWriteGate>
              <Button variant="outline" onClick={() => setUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                엑셀 업로드
              </Button>
            </MenuWriteGate>
            <Button
              variant="outline"
              onClick={handleBulkTransferDownload}
              disabled={
                !search.yearMonth ||
                !canDownload ||
                downloadBulkTransfer.isPending
              }
            >
              <Download className="mr-2 h-4 w-4" />
              대량 이체 다운로드
            </Button>
            <MenuWriteGate>
              <Button
                variant="outline"
                onClick={handleCancelIssued}
                disabled={selectedIds.length === 0 || cancelInvoices.isPending}
              >
                <Ban className="mr-2 h-4 w-4" />
                선택 발행 취소
              </Button>
              <Button
                variant="outline"
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                선택 삭제
              </Button>
              <Button onClick={handleIssue} disabled={issueInvoices.isPending}>
                <Send className="mr-2 h-4 w-4" />
                선택 역발행 ({selectedIds.length})
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <FilterCard>
          <FilterRow gap={16} onSubmit={handleSearch}>
            <Field label="정산 년월" htmlFor="reverse-tax-ym">
              <MonthPicker
                id="reverse-tax-ym"
                value={yearMonthInput}
                onChange={handleYearMonthChange}
                className="w-36"
              />
            </Field>
            <Field label="검색어" htmlFor="reverse-tax-keyword">
              <Input
                id="reverse-tax-keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="사용자 이메일, 업체명"
                className="w-48"
              />
            </Field>
            <Field label="내역 구분">
              <Select value={sourceTypeInput} onValueChange={setSourceTypeInput}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  <SelectItem value="SETTLEMENT">정산</SelectItem>
                  <SelectItem value="EXCEL">추가</SelectItem>
                  <SelectItem value="MANUAL">수동</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
            </Button>
          </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="역발행 목록을 불러오는 중..." />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 역발행 내역이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>정산 년월</TableHead>
                  <TableHead>작성일자</TableHead>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>업체명</TableHead>
                  <TableHead>확정금액</TableHead>
                  <TableHead>구분</TableHead>
                  <TableHead>발행상태</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleSelect(row.id)}
                      />
                    </TableCell>
                    <TableCell>{row.settlementYearMonth}</TableCell>
                    <TableCell>
                      {formatDate(fromTaxInvoiceWriteDate(row.writeDate))}
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.companyName}</TableCell>
                    <TableCell>{row.fixedAmount?.toLocaleString()}</TableCell>
                    <TableCell>
                      {SOURCE_LABELS[row.sourceType] ?? row.sourceType}
                    </TableCell>
                    <TableCell>
                      {ISSUE_STATUS_LABELS[row.issueStatus] ?? row.issueStatus}
                    </TableCell>
                    <TableCell>
                      <MenuWriteGate>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(row.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </MenuWriteGate>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </DataCard>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>역발행 엑셀 업로드</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            정산년월, 작성일자, 사용자 계정, 확정금액 등이 포함된 xlsx 파일을
            업로드합니다.
          </p>
          <FileUploadButton
            inputRef={uploadInputRef}
            accept=".xlsx,.xls"
            resetOnSelect={false}
            onFilesSelected={(files) => setUploadFile(files?.[0] ?? null)}
          >
            파일 선택
          </FileUploadButton>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              취소
            </Button>
            <Button
              onClick={handleUploadExcel}
              disabled={uploadTaxInvoices.isPending}
            >
              업로드
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>정산 정보 초기화</AlertDialogTitle>
            <AlertDialogDescription>
              수정된 내역은 초기화됩니다. 정산 정보를 초기화하시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPublished}>
              초기화
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
